import moment from 'moment';
/**
 * Onboarding utilities for managing user onboarding state
 */

export const ONBOARDING_STORAGE_KEY = 'onboarding_completed';
export const ONBOARDING_VERSION_KEY = 'onboarding_version';
export const CURRENT_ONBOARDING_VERSION = '1.0';

/**
 * Check if user has completed onboarding
 * @param {Object} userMeta - Optional user metadata from login response
 * @returns {boolean}
 */
export function hasCompletedOnboarding(userMeta = null) {
  // First check localStorage
  const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
  const version = localStorage.getItem(ONBOARDING_VERSION_KEY);
  const localStorageCompleted = completed && version === CURRENT_ONBOARDING_VERSION;

  // Then check if user has completed the tour on the server (from login meta)
  if (userMeta && userMeta.tourTaken && Array.isArray(userMeta.tourTaken)) {
    const dashboardTour = userMeta.tourTaken.find(
      (tour) => tour.tourType === 'dashboard-onboarding' && tour.version === CURRENT_ONBOARDING_VERSION
    );
    if (dashboardTour) {
      return true;
    }
  }

  // Fall back to localStorage check
  return localStorageCompleted;
}

/**
 * Mark onboarding as completed
 */
export function markOnboardingCompleted() {
  localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
  localStorage.setItem(ONBOARDING_VERSION_KEY, CURRENT_ONBOARDING_VERSION);
}

/**
 * Mark tour as taken via API call
 * @param {Object} vueInstance - Vue component instance to access $http
 * @returns {Promise}
 */
export async function markTourAsTaken(vueInstance) {
  try {
    const response = await vueInstance.$http.post('/assemble/api/v1/account/portal/users/tour-taken', {
      tourType: 'dashboard-onboarding',
      version: CURRENT_ONBOARDING_VERSION,
      completedAt: moment().valueOf()
    });

    console.log('Tour marked as taken successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to mark tour as taken:', error);
    // Don't throw the error to avoid breaking the user experience
    // if the API call fails - the tour completion should still work
    return null;
  }
}

/**
 * Reset onboarding state (useful for testing)
 */
export function resetOnboarding() {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  localStorage.removeItem(ONBOARDING_VERSION_KEY);
}

/**
 * Check if user is a new user (no previous registration history)
 * @param {Object} userStore - Vuex user store
 * @returns {boolean}
 */
export function isNewUser(userStore) {
  // Check if user has any previous registrations
  const registrations = userStore.getters['user/getActiveMemberRegos'] || [];
  return registrations.length === 0;
}

/**
 * Get recommended first steps based on user state
 * @param {Object} userStore - Vuex user store
 * @returns {Array} Array of recommended actions
 */
export function getRecommendedFirstSteps(userStore) {
  const recommendations = [];

  if (isNewUser(userStore)) {
    recommendations.push({
      action: 'browse-featured',
      title: 'Browse Popular Events',
      description: 'Check out what\'s popular in your area'
    });

    recommendations.push({
      action: 'search-all',
      title: 'Search All Options',
      description: 'Find exactly what you\'re looking for'
    });

    recommendations.push({
      action: 'personalize',
      title: 'Get Personalised Results',
      description: 'Answer a few questions for better recommendations'
    });
  }

  return recommendations;
}

/**
 * Track onboarding step completion for analytics
 * @param {string} stepId - The step that was completed
 * @param {string} action - The action taken (completed, skipped, etc.)
 */
export function trackOnboardingStep(stepId, action = 'completed') {
  // In a real app, this would send to analytics
  console.log(`Onboarding step ${stepId} ${action}`);

  // Store locally for debugging
  const steps = JSON.parse(localStorage.getItem('onboarding_steps') || '[]');
  steps.push({
    stepId,
    action,
    timestamp: new Date().toISOString()
  });
  localStorage.setItem('onboarding_steps', JSON.stringify(steps));
}

/**
 * Get onboarding analytics data
 * @returns {Array} Array of completed steps
 */
export function getOnboardingAnalytics() {
  return JSON.parse(localStorage.getItem('onboarding_steps') || '[]');
}
